// React-Query hooks для Sprint 4 upload-эндпойнтов (A-1..A-6).
// Source of truth: docs/specs/sprint-4/stage-4-frontend.md §2 + stage-1-contracts.md §8.3-§8.5.
//
// КЛЮЧЕВЫЕ КОНТРАКТЫ:
//   - useStoreUpload НЕ редиректит — handler в UploadSheet делает await + router.push
//     (паттерн Sprint 3 race protection).
//   - useUploadPolling: backoff 2s → 5s → 10s, banner-таймаут 30s (UX-S4-2).
//     Polling продолжается после таймаута — banner ставит сам Processing-экран.
//   - useUpload / useUploadPolling unwrap'ят { upload: Upload } → Upload, чтобы
//     consumer мог читать data.status напрямую.
//   - Invalidate ['uploads'] без exact:true мэтчит префиксно ['uploads', {status:...}],
//     это нужно R-2 OnboardingRouter — после confirm/report ключ сбрасывается.

import { useRef } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type {
  CreateBatchResponse,
  UploadBatchResponse,
  ConfirmUploadRequest,
  ConfirmUploadResponse,
  DeleteUploadResponse,
  DestroyUploadResponse,
  GetUploadResponse,
  ListUploadsResponse,
  ReopenUploadResponse,
  ReportUploadRequest,
  ReportUploadResponse,
  ReviewEditsRequest,
  StoreUploadAcceptedResponse,
  UpdateReviewResponse,
  UpdateUploadRequest,
  UpdateUploadResponse,
  Upload,
  UploadApiError,
  UploadStatus,
} from '@/api/types';

// Banner-таймаут: после этого Processing показывает «обработка занимает чуть больше времени».
// Polling продолжается дальше — это исключительно UX-индикатор, не hard-stop.
export const POLL_TIMEOUT_MS = 30_000;

// A1: upload — это отправка файла + синхронная запись на диск VPS, которая под
// нагрузкой/swap'е бывает медленной (см. инцидент 19.06: запись растянулась на 65с).
// Глобальный axios-timeout 20s тут слишком агрессивен и рвёт фактически успешную
// загрузку. Per-request override только для POST /uploads; GET'ы остаются на 20s.
export const UPLOAD_TIMEOUT_MS = 60_000;

/**
 * Pure helper для polling backoff. Exported для §7.3 unit-тестов.
 */
export function computeNextPollInterval(
  data: { status: UploadStatus } | undefined,
  elapsedMs: number,
): number | false {
  if (!data) return 2_000;
  if (data.status !== 'processing') return false;
  if (elapsedMs < 5_000) return 2_000;
  if (elapsedMs < 30_000) return 5_000;
  return 10_000;
}

export function useStoreUpload(): UseMutationResult<
  StoreUploadAcceptedResponse,
  AxiosError<UploadApiError>,
  { file: File; comment?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, comment }) => {
      const fd = new FormData();
      fd.append('file', file);
      if (comment) fd.append('comment', comment);
      const { data } = await api.post<StoreUploadAcceptedResponse>('/uploads', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: UPLOAD_TIMEOUT_MS,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.uploads });
    },
  });
}

export function useUploadPolling(
  uploadId: number,
): UseQueryResult<GetUploadResponse, AxiosError<UploadApiError>> {
  const mountTimeRef = useRef<number>(Date.now());
  return useQuery({
    queryKey: queryKeys.upload(uploadId),
    queryFn: async () => (await api.get<GetUploadResponse>(`/uploads/${uploadId}`)).data,
    refetchInterval: (query) =>
      computeNextPollInterval(query.state.data?.upload, Date.now() - mountTimeRef.current),
    refetchIntervalInBackground: false,
  });
}

export function useUpload(
  uploadId: number,
  opts?: { enabled?: boolean },
): UseQueryResult<GetUploadResponse, AxiosError<UploadApiError>> {
  return useQuery({
    queryKey: queryKeys.upload(uploadId),
    queryFn: async () => (await api.get<GetUploadResponse>(`/uploads/${uploadId}`)).data,
    enabled: opts?.enabled ?? true,
    // HEAL-81: 404 — это ответ, а не сбой. Общая настройка повторяет любой отказ
    // один раз; для «анализа нет» повтор бессмыслен и лишь откладывает экран с
    // правильным текстом на время второго запроса.
    retry: (failureCount, err) => err?.response?.status !== 404 && failureCount < 1,
  });
}

export function useUploadHistory(
  filters?: { status?: UploadStatus },
  opts?: { enabled?: boolean },
): UseQueryResult<ListUploadsResponse, AxiosError<UploadApiError>> {
  const key = filters?.status ? queryKeys.uploadsByStatus(filters.status) : queryKeys.uploads;
  return useQuery({
    queryKey: key,
    queryFn: async () =>
      (await api.get<ListUploadsResponse>('/uploads', { params: filters })).data,
    enabled: opts?.enabled ?? true,
  });
}

/**
 * Pure factory для useMutation options (T-S5-14 testability — RF-S5-S4-24).
 * Mirror Sprint 4 RF-25 pattern (mountTimeRef extraction).
 *
 * Sprint 5 invalidation cascade (§2.4 / stage-1 §7.2):
 *   confirm → ['upload', id] + ['uploads'] + ['dashboard'] + ['biomarkers']
 *   report  → ['upload', id] + ['uploads'] + ['dashboard']     (БЕЗ biomarkers, RF-S5-21)
 *   reopen  → ['upload', id] + ['uploads'] + ['dashboard'] + ['biomarkers']
 */
export function makeConfirmUploadOptions(
  qc: QueryClient,
  uploadId: number,
): UseMutationOptions<ConfirmUploadResponse, AxiosError<UploadApiError>, ConfirmUploadRequest> {
  return {
    mutationFn: async (body) =>
      (await api.post<ConfirmUploadResponse>(`/uploads/${uploadId}/confirm`, body)).data,
    onSuccess: async () => {
      // HEALTHLAB-SPRINT-5: invalidation cascade ПЕРЕД navigate (D-S5-13 Часть 2).
      // useOnboardingState читает analyses_count из useDashboard() — stale cache →
      // юзер видит HomeConfirmed без свежего analysis.
      // Sprint 5A.2 (R3 H2): biomarkerInsights umbrella — backend BiomarkerInsightsInvalidator
      // вычистил cache rows в BiomarkerPointsWriter::writeFromUpload(); frontend парный
      // invalidate помечает queryClient cache stale → next GET regen с fresh hash.
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.upload(uploadId) }),
        qc.invalidateQueries({ queryKey: queryKeys.uploads }),
        qc.invalidateQueries({ queryKey: queryKeys.dashboard }),
        qc.invalidateQueries({ queryKey: queryKeys.biomarkers }),
        qc.invalidateQueries({ queryKey: queryKeys.biomarkerInsights }),
      ]);
    },
  };
}

export function makeReportUploadOptions(
  qc: QueryClient,
  uploadId: number,
): UseMutationOptions<ReportUploadResponse, AxiosError<UploadApiError>, ReportUploadRequest> {
  return {
    mutationFn: async (body) =>
      (await api.post<ReportUploadResponse>(`/uploads/${uploadId}/report`, body)).data,
    onSuccess: async () => {
      // RF-S5-21: НЕ ['biomarkers'] — report не меняет browse-список.
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.upload(uploadId) }),
        qc.invalidateQueries({ queryKey: queryKeys.uploads }),
        qc.invalidateQueries({ queryKey: queryKeys.dashboard }),
      ]);
    },
  };
}

export function makeReopenUploadOptions(
  qc: QueryClient,
  uploadId: number,
): UseMutationOptions<ReopenUploadResponse, AxiosError<UploadApiError>, void> {
  return {
    mutationFn: async () => {
      const { data } = await api.post<ReopenUploadResponse>(`/uploads/${uploadId}/reopen`);
      return data;
    },
    onSuccess: async () => {
      // Сохраняем Sprint 4 detail-key invalidation (RF-S5-S4-6 round 1 Medium):
      // Processing.tsx может всё ещё держать stale ['upload', id].
      // Sprint 5A.2 (R5 M1): biomarkerInsights umbrella — reopen меняет hygiene predicate
      // success→awaiting_user_review, biomarker_points excluded из digest → backend
      // BiomarkerInsightsInvalidator вычистил cache; frontend парный invalidate.
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.upload(uploadId) }),
        qc.invalidateQueries({ queryKey: queryKeys.uploads }),
        qc.invalidateQueries({ queryKey: queryKeys.dashboard }),
        qc.invalidateQueries({ queryKey: queryKeys.biomarkers }),
        qc.invalidateQueries({ queryKey: queryKeys.biomarkerInsights }),
      ]);
    },
  };
}

/**
 * HEAL-44a §5.4 — правка метаданных анализа.
 *
 * Каскад гасим полностью только когда менялась дата: она переставляет анализ в
 * истории и может изменить, какое значение показателя считается текущим. Правка
 * одного названия витрин не касается, и сбрасывать из-за неё весь дашборд —
 * лишняя работа на каждое переименование.
 */
export function makeUpdateUploadOptions(
  qc: QueryClient,
  uploadId: number,
): UseMutationOptions<UpdateUploadResponse, AxiosError<UploadApiError>, UpdateUploadRequest> {
  return {
    mutationFn: async (body) =>
      (await api.patch<UpdateUploadResponse>(`/uploads/${uploadId}`, body)).data,
    onSuccess: async (_data, variables) => {
      const keys = [
        qc.invalidateQueries({ queryKey: queryKeys.upload(uploadId) }),
        qc.invalidateQueries({ queryKey: queryKeys.uploads }),
      ];

      if (variables.measured_at !== undefined) {
        keys.push(
          qc.invalidateQueries({ queryKey: queryKeys.dashboard }),
          qc.invalidateQueries({ queryKey: queryKeys.biomarkers }),
          qc.invalidateQueries({ queryKey: queryKeys.biomarkerInsights }),
        );
      }

      await Promise.all(keys);
    },
  };
}

/**
 * HEAL-44a §5.3 — удаление анализа.
 *
 * Гасим тот же набор ключей, что и подтверждение: удаление снимает показатели,
 * то есть меняет ровно то же, что их появление. Это и есть критерий приёмки 7
 * («разделы обновляются без перезапуска приложения»).
 */
export function makeDeleteUploadOptions(
  qc: QueryClient,
  uploadId: number,
): UseMutationOptions<DeleteUploadResponse, AxiosError<UploadApiError>, void> {
  return {
    mutationFn: async () => {
      try {
        return (await api.delete<DeleteUploadResponse>(`/uploads/${uploadId}`)).data;
      } catch (e) {
        // 404 на удалении — тоже успех: анализа уже нет, результат тот, которого
        // человек добивался. Показывать ему ошибку было бы враньём.
        const status = (e as AxiosError).response?.status;
        if (status === 404) {
          return { message: 'Анализ удалён', deleted: { points: 0 } };
        }
        throw e;
      }
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.upload(uploadId) }),
        qc.invalidateQueries({ queryKey: queryKeys.uploads }),
        qc.invalidateQueries({ queryKey: queryKeys.dashboard }),
        qc.invalidateQueries({ queryKey: queryKeys.biomarkers }),
        qc.invalidateQueries({ queryKey: queryKeys.biomarkerInsights }),
      ]);
    },
  };
}

/** HEAL-44b — правки распознанных строк. Ответ сервера — источник истины для экрана. */
export function makeUpdateReviewOptions(
  qc: QueryClient,
  uploadId: number,
): UseMutationOptions<UpdateReviewResponse, AxiosError<UploadApiError>, ReviewEditsRequest> {
  return {
    mutationFn: async (body) =>
      (await api.patch<UpdateReviewResponse>(`/uploads/${uploadId}/review`, body)).data,
    onSuccess: (data, variables) => {
      // Ответ сервера кладём прямо в кэш, а не инвалидируем: перерисовка должна
      // быть мгновенной, и она обязана совпасть с тем, что сервер реально
      // сохранил. Рефетч дал бы лишний запрос и промежуточный кадр со старыми
      // цветами. Витрины не трогаем — точек ещё нет.
      qc.setQueryData<GetUploadResponse>(queryKeys.upload(uploadId), (prev) =>
        prev === undefined
          ? prev
          : {
              ...prev,
              upload: {
                ...prev.upload,
                user_edits:
                  (variables.removed?.length ?? 0) === 0 && (variables.items?.length ?? 0) === 0
                    ? null
                    : {
                        edited_at: new Date().toISOString(),
                        removed: variables.removed ?? [],
                        items: variables.items ?? [],
                      },
              },
              enriched_biomarkers: data.enriched_biomarkers,
              summary: data.summary,
            },
      );
    },
  };
}

export function useUpdateUpload(
  uploadId: number,
): UseMutationResult<UpdateUploadResponse, AxiosError<UploadApiError>, UpdateUploadRequest> {
  const qc = useQueryClient();
  return useMutation(makeUpdateUploadOptions(qc, uploadId));
}

export function useDeleteUpload(
  uploadId: number,
): UseMutationResult<DeleteUploadResponse, AxiosError<UploadApiError>, void> {
  const qc = useQueryClient();
  return useMutation(makeDeleteUploadOptions(qc, uploadId));
}

export function useUpdateReview(
  uploadId: number,
): UseMutationResult<UpdateReviewResponse, AxiosError<UploadApiError>, ReviewEditsRequest> {
  const qc = useQueryClient();
  return useMutation(makeUpdateReviewOptions(qc, uploadId));
}

export function useConfirmUpload(
  uploadId: number,
): UseMutationResult<ConfirmUploadResponse, AxiosError<UploadApiError>, ConfirmUploadRequest> {
  const qc = useQueryClient();
  return useMutation(makeConfirmUploadOptions(qc, uploadId));
}

export function useReportUpload(
  uploadId: number,
): UseMutationResult<ReportUploadResponse, AxiosError<UploadApiError>, ReportUploadRequest> {
  const qc = useQueryClient();
  return useMutation(makeReportUploadOptions(qc, uploadId));
}

/**
 * Sprint 5 A-S5-5: G3 re-report flow (`reported` → `awaiting_user_review`).
 * Pattern mirror Sprint 4 useConfirmUpload(uploadId) — uploadId в hook param,
 * mutation variables = void (RF-S5-S4-7 sync). После success caller делает
 * setLocation('/') → OnboardingRouter → HomePending.
 *
 * ⚠️ HEAL-23: UI-точки входа сейчас НЕТ — кнопка «Решить» в «Моих загрузках»
 * убрана (юзер после жалобы ждёт нашей проверки, а не своих действий). Хук и
 * эндпоинт `POST /uploads/{id}/reopen` живые и нужны будущему админ-процессу
 * разбора `reported` — не удалять как «мёртвый код».
 */
export function useReopenUpload(
  uploadId: number,
): UseMutationResult<ReopenUploadResponse, AxiosError<UploadApiError>, void> {
  const qc = useQueryClient();
  return useMutation(makeReopenUploadOptions(qc, uploadId));
}

export function useDestroyUpload(): UseMutationResult<
  DestroyUploadResponse,
  AxiosError<UploadApiError>,
  number
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uploadId) =>
      (await api.delete<DestroyUploadResponse>(`/uploads/${uploadId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.uploads }),
  });
}

// ─── HEAL-46a: пакет ────────────────────────────────────────────────────────

/**
 * Создать пакет ДО отправки файлов.
 *
 * Отдельный шаг, а не признак в первом файле: экран пакета должен существовать с
 * момента, когда человек нажал «Загрузить», — иначе при обрыве на первом же файле
 * ему некуда вернуться и нечего показать.
 */
export async function createUploadBatch(filesTotal: number): Promise<number> {
  const { data } = await api.post<CreateBatchResponse>('/uploads/batches', {
    files_total: filesTotal,
  });

  return data.batch_id;
}

/** Отправить один файл пакета. Возвращает исход, нужный отправщику. */
export async function postBatchFile(
  file: { blob: Blob; name: string; mimeType: string },
  meta: { batchId: number; position: number },
): Promise<{ uploadId: number; deferred: boolean }> {
  const fd = new FormData();
  fd.append('file', new File([file.blob], file.name, { type: file.mimeType }));
  fd.append('batch_id', String(meta.batchId));
  fd.append('batch_position', String(meta.position));

  const { data } = await api.post<StoreUploadAcceptedResponse>('/uploads', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT_MS,
  });

  return { uploadId: data.upload_id, deferred: data.parse_deferred === true };
}

/**
 * Состав пакета. Опрашивается, пока хоть один файл в работе.
 *
 * Как только всё дошло до конечного положения, опрос прекращается: держать
 * поллинг на завершённом пакете незачем.
 */
export function useUploadBatch(batchId: number): UseQueryResult<UploadBatchResponse, AxiosError> {
  return useQuery({
    queryKey: queryKeys.uploadBatch(batchId),
    queryFn: async () => (await api.get<UploadBatchResponse>(`/uploads/batches/${batchId}`)).data,
    refetchInterval: (query) => {
      // Опрос прекращается и когда пакета больше нет: иначе удалённый пакет
      // дёргался бы каждые пять секунд до закрытия экрана.
      if (query.state.error?.response?.status === 404) return false;

      return query.state.data?.is_complete ? false : 5_000;
    },

    // ⚠️ Общие настройки кэша этому экрану не подходят, и особенно — с push.
    //
    // По умолчанию данные считаются свежими 30 секунд, а при возврате в
    // приложение не перезапрашиваются вовсе. Человек уходил с экрана, пока
    // файлы обрабатывались, получал уведомление «Анализы обработаны», тапал —
    // и видел снимок, снятый ДО того, как распознавание закончилось. Ещё
    // несколько секунд экран честно показывал «обрабатывается» (найдено на
    // устройстве 24.09).
    //
    // Состояние пакета меняется на сервере постоянно, и держать его «свежим»
    // по таймеру бессмысленно: единственный достоверный ответ — только что
    // полученный.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,

    // HEAL-76 (D-5): «пакета нет» и «связь оборвалась» — разные вещи, и повтор
    // на 404 лишь откладывает экран с правильным текстом. Ссылка из ленты
    // сделала этот случай обычным: уведомление живёт год, пакет можно удалить
    // в любой момент. Тот же приём, что для анализа в HEAL-81.
    retry: (failureCount, err) => err?.response?.status !== 404 && failureCount < 1,
  });
}

/** Удаление пакета целиком — оно же отмена во время отправки (D-23). */
export async function deleteUploadBatch(batchId: number): Promise<number> {
  const { data } = await api.delete<{ deleted: { uploads: number; points: number } }>(
    `/uploads/batches/${batchId}`,
  );

  return data.deleted.uploads;
}
